import React, { useEffect, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { FONTS, COLORS, BREAKPOINTS, SHADOWS } from "../../utils/styleConstants";
import { formatDateOnly } from '../../utils/dateUtils';
import { formatAmountForDisplay } from '../../utils/amountUtils';

// Tono azul oscuro - ajustado para ser ligeramente más oscuro
const DARK_BLUE = "#20376e";
// Orange color for accents
const ACCENT_ORANGE = "#ff7057";

// Estilos globales para el scrollbar - enfoque completamente diferente
const GlobalScrollbarStyle = createGlobalStyle`
  .modal-open {
    overflow: hidden;
  }
  
  /* Estilo base para el área con scroll */
  .modal-scroll-area {
    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: ${ACCENT_ORANGE} transparent;
    
    /* Eliminar las flechas del scrollbar en todos los navegadores */
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background-color: ${ACCENT_ORANGE};
      border-radius: 4px;
    }
    
    /* Ocultar botones */
    &::-webkit-scrollbar-button {
      width: 0;
      height: 0;
      display: none;
    }
    
    /* Específicamente ocultar los botones de inicio y fin */
    &::-webkit-scrollbar-button:start:decrement,
    &::-webkit-scrollbar-button:end:increment,
    &::-webkit-scrollbar-button:vertical:start:decrement,
    &::-webkit-scrollbar-button:vertical:end:increment,
    &::-webkit-scrollbar-button:horizontal:start:decrement,
    &::-webkit-scrollbar-button:horizontal:end:increment {
      display: none;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const ModalContent = styled.div`
  position: relative;
  background-color: white;
  border-radius: 0.1rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 1100px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  outline: none;
  padding-right: 1.5rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-height: 80vh;
    margin: 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: none;
`;

const ModalScrollContent = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  padding-right: 6px; /* Aumentar el padding para dar más espacio */
  
  /* Estilos consistentes con los globales */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${ACCENT_ORANGE};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-button,
  &::-webkit-scrollbar-button:start:decrement,
  &::-webkit-scrollbar-button:end:increment,
  &::-webkit-scrollbar-button:vertical:start:decrement,
  &::-webkit-scrollbar-button:vertical:end:increment,
  &::-webkit-scrollbar-button:horizontal:start:decrement,
  &::-webkit-scrollbar-button:horizontal:end:increment {
    display: none;
    width: 0;
    height: 0;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: 2px solid ${ACCENT_ORANGE};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ACCENT_ORANGE};
  font-size: 1.6rem;
  cursor: pointer;
  margin-right: 1rem;
  padding: 0;
  line-height: 0.75;
  
  &:hover {
    background-color: #fff0e8;
  }
  
  &:focus {
    outline: none;
  }
`;

const HeaderTitle = styled.h2`
  font-size: 1rem;
  font-weight: 400;
  color: ${ACCENT_ORANGE};
  margin: 0;
  font-family: ${FONTS.PRIMARY};
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 6.5rem 1rem 6.5rem;
  border-bottom: none;
`;

const BrandLeft = styled.div`
  display: flex;
  align-items: center;
`;

const BrandName = styled.div`
  font-family: ${FONTS.PRIMARY};
  font-size: 1.5rem;
  font-weight: normal;
  
  span:first-child {
    color: #333;
    font-weight: normal;
  }
  
  span:last-child {
    color: #333;
    font-weight: 800;
  }
`;

const HeaderInfo = styled.div`
  text-align: left;
  font-family: ${FONTS.SECONDARY};
`;

const RefLine = styled.div`
  font-size: 0.75rem;
  font-weight: bold;
  color: rgb(0,0,73);
  margin-bottom: 0.3rem;
`;

const OfferTypeStyled = styled.div`
  font-size: 0.75rem;
  margin-bottom: 0.3rem;
`;

const BlueText = styled.span`
  color: rgb(0,0,73);
  font-weight: bold;
`;

const OrangeText = styled.span`
  color: #ff7057;
  font-weight: bold;
`;

const DeadlineStyled = styled.div`
  font-size: 0.75rem;
  color: #ff7057;
  margin-bottom: 0;
`;

const ModalBody = styled.div`
  padding: 1.5rem 6.5rem 1.5rem 6.5rem;
`;

const InfoBlock = styled.div`
  margin-bottom: 1rem;
  border-bottom: 1px solid #A8C7E7;
  padding-bottom: 1rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const BlockTitle = styled.h3`
  font-size: 1rem;
  font-weight: 400;
  color: #A8C7E7;
  margin-bottom: 0.75rem;
  font-family: ${FONTS.PRIMARY};
  letter-spacing: 0.01em;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  // gap: 0.5rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoItem = styled.div`
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.SECONDARY};
  font-weight: 400;
  // padding: 0.25rem;
`;

const NameItem = styled(InfoItem)`
  grid-column: 1;
  font-weight: 500;
`;

const IdItem = styled(InfoItem)`
  grid-column: 2;
`;

const EmailItem = styled(InfoItem)`
  grid-column: 3;
`;

const PhoneItem = styled(InfoItem)`
  grid-column: 4;
`;

const LabelItem = styled(InfoItem)`
  font-weight: 400;
`;

const BoldInfoItem = styled(InfoItem)`
  font-weight: bold;
`;

const ContentSection = styled.div`
  margin-bottom: 1.5rem;
`;

const ContentText = styled.div`
  font-size: 0.9rem;
  white-space: pre-line;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.SECONDARY};
  font-weight: 600;
`;

const OfferHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-top: 1rem;
`;

const OfferTitleSection = styled.div`
  display: flex;
  align-items: baseline;
`;

const OfferTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 800;
  color: ${ACCENT_ORANGE};
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  margin-right: 0.5rem;
  letter-spacing: 0.01em;
`;

const ConfidentialText = styled.span`
  font-size: 1.3rem;
  font-weight: 400;
  color: ${ACCENT_ORANGE};
  font-family: ${FONTS.SECONDARY};
  margin-left: 0.5rem;
`;

const SentByText = styled.div`
  font-size: 1rem;
  font-weight: 400;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
`;

// Status timeline components (copied from OfferTimeline.js)
const StatusContainer = styled.div`
  background: white;
  margin-bottom: 1rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  width: fit-content;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TimelineItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: fit-content;
  position: relative;
`;

const StateItem = styled.div`
  position: relative;
  width: fit-content;
  flex: 0 0 auto;
  padding-right: ${(props) => (props.isLast ? "0" : "1.5rem")};

  &::after {
    content: "▶";
    color: ${(props) => (props.isLast ? COLORS.ACCENT : COLORS.PRIMARY)};
    font-size: 0.8rem;
    position: absolute;
    right: 8px;
    top: 0;
    display: ${(props) => (props.isLast ? "none" : "block")};

    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const LineSegment = styled.div`
  height: ${(props) => (props.isLast ? "2px" : "1px")};
  background-color: ${(props) =>
    props.isLast ? COLORS.ACCENT : COLORS.PRIMARY};
  width: 100%;
  position: relative;
  margin: 0;
  align-self: flex-start;
`;

const DateItem = styled.div`
  width: fit-content;
  flex: 0 0 auto;
  text-align: left;
`;

const StatusText = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(props) => (props.isLast ? COLORS.ACCENT : COLORS.PRIMARY)};
  font-family: ${FONTS.SECONDARY};
  text-transform: capitalize;
`;

const StatusDate = styled.div`
  font-size: 0.8rem;
  color: ${(props) => (props.isLast ? COLORS.ACCENT : COLORS.PRIMARY)};
  font-family: ${FONTS.SECONDARY};
  line-height: 1.2;
`;

const FileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 0.5rem;
`;

const FileIcon = styled.div`
  color: #75a9f9;
  margin-right: 0.5rem;
  
  svg {
    width: 16px;
    height: 16px;
    transform: rotate(-45deg);
  }
`;

const FileName = styled.span`
  font-size: 0.9rem;
  font-weight: 400;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.SECONDARY};
`;

const RichContent = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.SECONDARY};
  font-weight: 600;
  
  p {
    margin-bottom: 0.75rem;
  }
  
  ul, ol {
    margin-left: 1.25rem;
    margin-bottom: 0.75rem;
  }
  
  strong {
    font-weight: 800;
  }
`;

const PreviewModal = ({ isOpen, onClose, formData, user, offerData = null, isCounterOffer = false }) => {
  const closeBtnRef = useRef(null);
  const modalRef = useRef(null);
  const scrollContentRef = useRef(null);
  
  // Add class when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      if (scrollContentRef.current) {
        scrollContentRef.current.classList.add('modal-scroll-area');
      }
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);
  
  // Debug: Log the data passed to the modal
  useEffect(() => {
    if (isOpen) {
      console.log("PreviewModal rendered with formData:", formData);
      console.log("PreviewModal rendered with offerData:", offerData);
      console.log("User data for representante:", user);
      console.log("isRealOffer:", !!offerData || (formData?.id && formData?.status));
      
      // Log critical data fields to help diagnose issues
      if (formData) {
        console.log("Preview formData summary:");
        console.log("- Ofertante:", formData.ofertante?.nombre, formData.ofertante?.apellidos);
        console.log("- Contraparte:", formData.contraparte?.nombre, formData.contraparte?.apellidos);
        console.log("- Objeto:", formData.objeto);
        console.log("- Cuantía:", formData.cuantia);
        console.log("- Confidencialidad:", formData.confidencialidad);
      }
      
      if (offerData) {
        console.log("Preview offerData summary:");
        console.log("- ID:", offerData.id);
        console.log("- Status:", offerData.status);
        console.log("- Creation Date:", offerData.creationDate || offerData.creation_date || offerData.created_at);
        console.log("- Sent Date:", offerData.sentDate || offerData.sent_date || offerData.sent_at);
        console.log("- All offer keys:", Object.keys(offerData));
      }
    }
  }, [isOpen, formData, offerData, user]);

  // Focus trap and ESC close
  useEffect(() => {
    if (!isOpen) return;
    // Focus close button on open
    closeBtnRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
      // Focus trap
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Check if formData is empty or undefined
  if (!formData) {
    console.error("No formData provided to PreviewModal");
    return (
      <ModalOverlay role="dialog" aria-modal="true" tabIndex={-1} onClick={onClose}>
        <GlobalScrollbarStyle />
        <ModalContent ref={modalRef} onClick={e => e.stopPropagation()} tabIndex={0}>
          <ModalHeader>
            <CloseButton ref={closeBtnRef} aria-label="Cerrar vista previa" onClick={onClose}>×</CloseButton>
            <HeaderTitle>Vista previa Oferta Vinculante</HeaderTitle>
          </ModalHeader>
          <ModalScrollContent ref={scrollContentRef}>
            <ModalBody>
              <ContentText>No se han podido cargar los datos del formulario.</ContentText>
            </ModalBody>
          </ModalScrollContent>
        </ModalContent>
      </ModalOverlay>
    );
  }

  // Prepare data from formData - use safe access with fallbacks
  const ofertanteData = formData?.ofertante || {};
  const contraparteData = formData?.contraparte || {};
  const representacionData = formData?.representacionContraparte || {};
  const objetoTexto = formData?.objeto || "";
  const confidencialidad = formData?.confidencialidad || false;
  const contenidoTexto = formData?.contenido || "";
  const archivoData = formData?.archivo;
  const referencia = formData?.referencia || "";
  const cuantia = formData?.cuantia || "";
  const plazoRespuesta = formData?.plazoRespuesta || "";

  // Confidentiality label
  const confidLabel = confidencialidad ? "Confidencial" : "No confidencial";

  // Format amount with currency symbol using unified function
  const formattedAmount = cuantia 
    ? (() => {
        // Si cuantia ya está formateado (contiene puntos), usarlo directamente
        if (typeof cuantia === 'string' && cuantia.includes('.')) {
          return cuantia + ' €';
        }
        // Si no está formateado, usar la función de formateo
        return formatAmountForDisplay(cuantia);
      })()
    : "-";
  
  // Format date
  const formattedDate = plazoRespuesta 
    ? formatDateOnly(plazoRespuesta)
    : "-";

  // Format date for status (copied from OfferTimeline.js)
  const formatStatusDate = (dateString) => {
    if (!dateString) return { date: "", time: "" };
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
    const timeStr = date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return { date: dateStr, time: timeStr };
  };

  // Status text mapping (copied from OfferTimeline.js)
  const getStatusText = (status) => {
    const statusMap = {
      draft: "Borrador",
      sent: "Enviada",
      accepted: "Aceptada",
      rejected: "Rechazada",
      expired: "Vencida",
      counteroffered: "Contraofertada",
      expiring_soon: "Vence en 5 días",
      reformulated: "Reformulado",
      reformulation_accepted: "Reformulado aceptado",
      reformulation_rejected: "Reformulado rechazado",
      signing: "Firmando",
      partial_signed: "Firmado parcialmente",
      signed: "Firmado",
    };
    return statusMap[status] || status;
  };

  // Build timeline for preview (adapted from OfferTimeline.js)
  const buildPreviewTimeline = (data, isRealOffer = false) => {
    const timeline = [];

    // If this is preview of a real offer, use that data
    if (isRealOffer && data) {
      const offer = data;
      
      // 1. BORRADOR - Always first state (creation date)
      const creationDate = offer.creationDate || offer.creation_date || offer.created_at || offer.offer_creation_date;
      timeline.push({
        status: "draft",
        date: creationDate || new Date().toISOString(),
        text: getStatusText("draft"),
      });

      // 2. ENVIADO - If offer was sent
      const sentDate = offer.sentDate || offer.sent_date || offer.sent_at;
      const lastUpdate = offer.lastStatusUpdateDate || offer.last_status_update_date || offer.updated_at;
      
      if (
        sentDate ||
        [
          "sent",
          "accepted",
          "rejected",
          "counteroffered",
          "reformulated",
          "reformulation_accepted",
          "reformulation_rejected",
          "finalized",
          "expired",
        ].includes(offer.status)
      ) {
        timeline.push({
          status: "sent",
          date: sentDate || lastUpdate || creationDate,
          text: getStatusText("sent"),
        });
      }

      // 3. CONTRAOFERTADO - If there's a counteroffer
      if (
        offer.counteroffer_date ||
        offer.counteroffer_user_id ||
        offer.counteroffer_text ||
        [
          "counteroffered",
          "reformulated",
          "reformulation_accepted", 
          "reformulation_rejected"
        ].includes(offer.status)
      ) {
        timeline.push({
          status: "counteroffered",
          date: offer.counteroffer_date || offer.lastStatusUpdateDate || offer.last_status_update_date,
          text: getStatusText("counteroffered"),
        });
      }

      // 4. REFORMULADO - If there's a reformulation
      if (
        offer.reformulation_date ||
        offer.reformulation_content ||
        offer.reformulationText ||
        [
          "reformulated",
          "reformulation_accepted",
          "reformulation_rejected"
        ].includes(offer.status)
      ) {
        timeline.push({
          status: "reformulated",
          date: offer.reformulation_date || offer.lastStatusUpdateDate || offer.last_status_update_date,
          text: getStatusText("reformulated"),
        });
      }

      // 5. Final states
      if (["accepted", "rejected", "expired", "signed"].includes(offer.status)) {
        timeline.push({
          status: offer.status,
          date: offer.lastStatusUpdateDate || offer.last_status_update_date || new Date().toISOString(),
          text: getStatusText(offer.status),
        });
      }
    } else {
      // For form preview, only show draft state
      timeline.push({
        status: "draft",
        date: new Date().toISOString(),
        text: getStatusText("draft"),
      });
    }

    return timeline;
  };

  // Get timeline for preview - use offerData if available, otherwise check formData
  const isRealOffer = !!offerData || (formData?.id && formData?.status);
  const timelineData = offerData || formData;
  const timeline = buildPreviewTimeline(timelineData, isRealOffer);

  // Get representante data from the logged-in user
  const representanteNombre = user?.first_name || user?.nombre || '';
  const representanteApellidos = user?.last_name || user?.apellidos || '';
  const representanteEmail = user?.email || '';
  const representanteIdentificacion = user?.id_number || user?.identificacion || '-';
  const representanteTelefono = user?.mobile_phone || user?.telefono || user?.movil || '-';

  // Lawyer extra fields - support both new API format and legacy format
  const lawyerInfo = user ? (
    <>
      {(user.lawyer_membership_number || user.numColegiado) && 
        <InfoItem>{user.lawyer_membership_number || user.numColegiado}</InfoItem>}
      {(user.province_name || user.provinciaColegio) && 
        <InfoItem>{user.province_name || user.provinciaColegio}</InfoItem>}
    </>
  ) : null;

  // Represented party block (if lawyer)
  const showRepresented = (user?.lawyer_membership_number || user?.numColegiado || user?.rol === "abogado") && representacionData?.activa;

  // Counterparty lawyer block (if provided)
  const showCounterLawyer = representacionData?.activa && (
    representacionData.nombre || 
    representacionData.apellidos || 
    representacionData.identificacion || 
    representacionData.email || 
    representacionData.movil
  );

  // Offer content: file or rich text
  const hasFile = !!archivoData;

  return (
    <ModalOverlay role="dialog" aria-modal="true" tabIndex={-1} onClick={onClose}>
      <GlobalScrollbarStyle />
      <ModalContent
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        tabIndex={0}
      >
        <ModalHeader>
          <CloseButton
            ref={closeBtnRef}
            aria-label="Cerrar vista previa"
            onClick={onClose}
          >
            ×
          </CloseButton>
          <HeaderTitle>Vista previa Oferta Vinculante</HeaderTitle>
        </ModalHeader>
        
        <ModalScrollContent ref={scrollContentRef}>
          <BrandContainer>
            <BrandLeft>
              <svg className="logo" style={{height: '24px', marginRight: '0.75rem', filter: 'brightness(0.7) contrast(1.4) saturate(1.2)'}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1128.24">
                <path fill="#95B9E3" d="M981.32,4.35l-356.27,501.59c-24.75,34.84-24.75,81.52,0,116.36l356.27,501.59c5.81,8.17,18.68,4.07,18.68-5.96V10.31c0-10.03-12.88-14.13-18.68-5.96Z"/>
                <path fill="#020048" d="M18.68,4.35l356.27,501.59c24.75,34.84,24.75,81.52,0,116.36L18.68,1123.89c-5.81,8.17-18.68,4.07-18.68-5.96V10.31C0,.29,12.88-3.82,18.68,4.35Z"/>
              </svg>
              <BrandName>
                <span>Ofertas</span>
                <span><strong>Vinculantes</strong></span>
              </BrandName>
            </BrandLeft>
            <HeaderInfo>
              <RefLine>REF.PROPIA {referencia || formData?.id || 'N/A'}</RefLine>
              <OfferTypeStyled>
                <BlueText>Oferta Vinculante</BlueText> <OrangeText>{confidLabel}</OrangeText>
              </OfferTypeStyled>
              <DeadlineStyled>Plazo respuesta ▶ {formattedDate}</DeadlineStyled>
            </HeaderInfo>
          </BrandContainer>
          
          <ModalBody>
            {/* Representative (logged-in user) section */}
            <InfoBlock>
              <BlockTitle>Representante de la parte ofertante</BlockTitle>
              <TableGrid>
                <BoldInfoItem>{representanteNombre || '-'}</BoldInfoItem>
                <BoldInfoItem>{representanteApellidos || '-'}</BoldInfoItem>
                <BoldInfoItem>{representanteEmail || '-'}</BoldInfoItem>
                <BoldInfoItem>{representanteTelefono || '-'}</BoldInfoItem>
                <LabelItem>Núm de Colegiado</LabelItem>
                <InfoItem></InfoItem>
                <LabelItem>DNI, NIE</LabelItem>
                <InfoItem></InfoItem>
                <BoldInfoItem>{user?.lawyer_membership_number || user?.numColegiado || '-'}</BoldInfoItem>
                <BoldInfoItem>{user?.province_name || user?.provinciaColegio || '-'}</BoldInfoItem>
                <BoldInfoItem>{representanteIdentificacion || '-'}</BoldInfoItem>
                <InfoItem></InfoItem>
              </TableGrid>
            </InfoBlock>
            
            {/* User info (renamed from "Tus datos" to "Datos de la parte ofertante") */}
            <InfoBlock>
              <BlockTitle>Datos de la parte ofertante</BlockTitle>
              <TableGrid>
                <BoldInfoItem>{ofertanteData.nombre || '-'}</BoldInfoItem>
                <BoldInfoItem>{ofertanteData.apellidos || '-'}</BoldInfoItem>
                <BoldInfoItem>{ofertanteData.email || '-'}</BoldInfoItem>
                <BoldInfoItem>{ofertanteData.movil || '-'}</BoldInfoItem>
                <LabelItem style={{ gridColumn: '1 / 5' }}>DNI, NIE, NIF/CIF, Pasaporte</LabelItem>
                <BoldInfoItem>{ofertanteData.identificacion || '-'}</BoldInfoItem>
              </TableGrid>
            </InfoBlock>
            
            {/* Dispute subject */}
            <InfoBlock>
              <BlockTitle>Objeto de la controversia</BlockTitle>
              <ContentText>{objetoTexto || '-'}</ContentText>
              <TableGrid>
                <InfoItem style={{ gridColumn: '1 / 3' }}>
                  Cuantía objeto de la controversía superior a 2.000 €
                </InfoItem>
                <InfoItem style={{ gridColumn: '3' }}>
                  {(() => {
                    // Parse amount to number for comparison
                    let numericAmount = 0;
                    if (cuantia) {
                      // Handle both string and number formats
                      const cleanAmount = typeof cuantia === 'string' 
                        ? cuantia.replace(/[.€\s]/g, '').replace(',', '.')
                        : cuantia.toString();
                      numericAmount = parseFloat(cleanAmount) || 0;
                    }
                    return numericAmount > 2000 ? 'Sí' : 'No';
                  })()}
                </InfoItem>
              </TableGrid>
              <TableGrid>
                <InfoItem style={{ gridColumn: '1 / 3' }}>
                  Cuantía objeto de la controversía
                </InfoItem>
                <InfoItem style={{ gridColumn: '3' }}>
                  {formattedAmount}
                </InfoItem>
              </TableGrid>
            </InfoBlock>
            
            {/* Counterparty */}
            <InfoBlock>
              <BlockTitle>Datos de la contraparte</BlockTitle>
              <TableGrid>
                <BoldInfoItem>{contraparteData.nombre || '-'}</BoldInfoItem>
                <BoldInfoItem>{contraparteData.apellidos || '-'}</BoldInfoItem>
                <BoldInfoItem>{contraparteData.email || '-'}</BoldInfoItem>
                <BoldInfoItem>{contraparteData.movil || '-'}</BoldInfoItem>
                <LabelItem style={{ gridColumn: '1 / 5' }}>DNI, NIE, NIF/CIF, Pasaporte</LabelItem>
                <BoldInfoItem>{contraparteData.identificacion || '-'}</BoldInfoItem>
              </TableGrid>
            </InfoBlock>
            
            {/* Counterparty lawyer (if provided) */}
            {showCounterLawyer && (
              <InfoBlock>
                <BlockTitle>Representante de la contraparte</BlockTitle>
                <TableGrid>
                  <BoldInfoItem>{representacionData.nombre || '-'}</BoldInfoItem>
                  <BoldInfoItem>{representacionData.apellidos || '-'}</BoldInfoItem>
                  <BoldInfoItem>{representacionData.email || '-'}</BoldInfoItem>
                  <BoldInfoItem>{representacionData.movil || '-'}</BoldInfoItem>
                  <LabelItem style={{ gridColumn: '1 / 5' }}>DNI, NIE</LabelItem>
                  <BoldInfoItem>{representacionData.identificacion || '-'}</BoldInfoItem>
                </TableGrid>
              </InfoBlock>
            )}
            
            {/* Offer title and confidentiality */}
            <OfferHeader>
              <OfferTitleSection>
                <OfferTitle>{isCounterOffer ? 'Contraoferta Vinculante' : 'Oferta Vinculante'}</OfferTitle>
                <ConfidentialText>{confidLabel}</ConfidentialText>
              </OfferTitleSection>
              <SentByText>
                Enviado por ▶ {`${ofertanteData.nombre || ''} ${ofertanteData.apellidos || ''}`.trim() || 'No especificado'}
              </SentByText>
            </OfferHeader>

            {/* Status Timeline */}
            <StatusContainer>
              {timeline.map((item, index) => {
                const { date, time } = formatStatusDate(item.date);
                const isLastItem = index === timeline.length - 1;
                
                return (
                  <TimelineItem key={index}>
                    <StateItem isLast={isLastItem}>
                      <StatusText isLast={isLastItem}>
                        {item.text}
                      </StatusText>
                    </StateItem>
                    <LineSegment isLast={isLastItem} />
                    <DateItem>
                      <StatusDate isLast={isLastItem}>
                        {date}
                        <br />
                        {time}
                      </StatusDate>
                    </DateItem>
                  </TimelineItem>
                );
              })}
            </StatusContainer>
            
            {/* Offer content */}
            <InfoBlock>
              {hasFile ? (
                <FileContainer>
                  <FileIcon>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                    </svg>
                  </FileIcon>
                  <FileName>{archivoData.name}</FileName>
                </FileContainer>
              ) : (
                <RichContent dangerouslySetInnerHTML={{ __html: contenidoTexto || '-' }} />
              )}
            </InfoBlock>
          </ModalBody>
        </ModalScrollContent>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PreviewModal; 